import type { DividendStockPriceHistoryCsv } from '@app-types/dividendApi';

/**
 * A single parsed row of the `chart5-hts` stock-price CSV. Only `date` and
 * `close` matter for the blue dividend chart line; the other OHLC/volume
 * columns are intentionally ignored.
 */
export interface ParsedDividendStockPricePoint {
  /** Raw 8-digit trade date (YYYYMMDD). */
  date: string;
  /** Year+month bucket used to align CSV rows with annual chart points. */
  monthKey: string;
  /** Closing price (fifth CSV column); null when missing/non-numeric. */
  close: number | null;
}

const YYYYMMDD = /^\d{8}$/;

/** `20260331` -> `202603` (year+month bucket). */
const toMonthKey = (yyyymmdd: string): string => yyyymmdd.slice(0, 6);

const parseClose = (raw: string | undefined): number | null => {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Parses the raw `chart5-hts` CSV string into date/close points, ascending by
 * date. Rows without an 8-digit date or fewer than five columns are skipped.
 * Rows with a missing/non-numeric close are kept with `close: null` so the
 * merge step can still see the date bucket.
 */
export const parseDividendStockPriceCsv = (
  csv: DividendStockPriceHistoryCsv | null | undefined,
): ParsedDividendStockPricePoint[] => {
  if (typeof csv !== 'string' || !csv.trim()) return [];

  const points: ParsedDividendStockPricePoint[] = [];
  for (const line of csv.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const columns = trimmed.split(',');
    if (columns.length < 5) continue;

    const date = columns[0]?.trim() ?? '';
    if (!YYYYMMDD.test(date)) continue;

    points.push({
      date,
      monthKey: toMonthKey(date),
      close: parseClose(columns[4]),
    });
  }

  return points.sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Builds a map of month bucket -> close price using the latest date within
 * each month. Rows with a null close are ignored so they cannot overwrite a
 * usable earlier close in the same month.
 */
export const buildStockCloseByMonth = (
  points: ParsedDividendStockPricePoint[],
): Map<string, number> => {
  const latestDateByMonth = new Map<string, string>();
  const closeByMonth = new Map<string, number>();

  for (const point of points) {
    if (point.close === null) continue;
    const previousDate = latestDateByMonth.get(point.monthKey);
    if (!previousDate || point.date >= previousDate) {
      latestDateByMonth.set(point.monthKey, point.date);
      closeByMonth.set(point.monthKey, point.close);
    }
  }

  return closeByMonth;
};
